/**
 * useTheme Composable - Laumeris
 * Gestion du thème sombre/clair
 */

import { ref, computed, watch, onMounted } from 'vue'

type Theme = 'light' | 'dark' | 'system'

// State (singleton)
const theme = ref<Theme>('system')
const isDark = ref(false)

// Check system preference
const getSystemPreference = (): boolean => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  }
  return false
}

// Update document class
const updateDocumentClass = (dark: boolean): void => {
  if (typeof document !== 'undefined') {
    if (dark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }
}

// Load theme from localStorage
const loadTheme = (): void => {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('theme') as Theme | null
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      theme.value = stored
    }
  }
}

// Save theme to localStorage
const saveTheme = (value: Theme): void => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('theme', value)
  }
}

export function useTheme() {
  // Initialize on mount
  onMounted(() => {
    loadTheme()

    // Listen for system preference changes
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      mediaQuery.addEventListener('change', (e) => {
        if (theme.value === 'system') {
          isDark.value = e.matches
          updateDocumentClass(e.matches)
        }
      })
    }
  })

  // Watch theme changes
  watch(theme, (newTheme) => {
    saveTheme(newTheme)

    if (newTheme === 'system') {
      isDark.value = getSystemPreference()
    } else {
      isDark.value = newTheme === 'dark'
    }

    updateDocumentClass(isDark.value)
  }, { immediate: true })

  // Set theme
  const setTheme = (newTheme: Theme): void => {
    theme.value = newTheme
  }

  // Toggle between light and dark
  const toggleTheme = (): void => {
    if (theme.value === 'system') {
      theme.value = getSystemPreference() ? 'light' : 'dark'
    } else {
      theme.value = theme.value === 'dark' ? 'light' : 'dark'
    }
  }

  // Computed for icons
  const themeIcon = computed(() => {
    switch (theme.value) {
      case 'dark':
        return 'moon'
      case 'light':
        return 'sun'
      default:
        return 'system'
    }
  })

  return {
    theme: computed(() => theme.value),
    isDark: computed(() => isDark.value),
    themeIcon,
    setTheme,
    toggleTheme
  }
}
